import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Cpu, HardDrive, MemoryStick, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import * as api from '../../services/api';

function ServerStatus() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.getServerStatus();
      setStatus(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  const formatBytes = (bytes) => {
    if (!bytes) return '—';
    const gb = bytes / (1024 * 1024 * 1024);
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    parts.push(`${m}m`);
    return parts.join(' ');
  };

  const pctColor = (pct) => {
    if (pct < 50) return '#10b981';
    if (pct < 80) return '#f59e0b';
    return '#ef4444';
  };

  // ── Health Assessment ──────────────────────────────────────────────────
  const getHealthAssessment = () => {
    if (!status) return null;
    const cpu = status.cpu.loadPct;
    const mem = status.memory.usedPct;
    const disk = parseInt(status.disk.pct) || 0;

    const issues = [];
    if (cpu > 90) issues.push(`CPU very high (${cpu}%)`);
    else if (cpu > 70) issues.push(`CPU under load (${cpu}%)`);
    if (mem > 95) issues.push(`Memory critically low`);
    else if (mem > 80) issues.push(`Memory usage high (${mem}%)`);
    if (disk > 95) issues.push(`Disk critically full (${status.disk.pct})`);
    else if (disk > 85) issues.push(`Disk space low (${status.disk.pct})`);
    if (status.streams.active > 50) issues.push(`High stream count (${status.streams.active} active)`);

    const critical = cpu > 90 || mem > 95 || disk > 95;
    const warning = cpu > 70 || mem > 80 || disk > 85 || status.streams.active > 50;

    if (critical) {
      return {
        level: 'critical',
        icon: XCircle,
        label: 'Streaming Issues Likely',
        detail: issues.join(' · ') || 'Server under critical load — streams may be affected',
        bg: '#fef2f2',
        border: '#fecaca',
        text: '#dc2626',
        iconBg: '#fee2e2'
      };
    }
    if (warning) {
      return {
        level: 'warning',
        icon: AlertTriangle,
        label: 'Server Under Load',
        detail: issues.join(' · ') || 'Elevated resource usage — monitor closely',
        bg: '#fffbeb',
        border: '#fde68a',
        text: '#d97706',
        iconBg: '#fef3c7'
      };
    }
    return {
      level: 'good',
      icon: CheckCircle,
      label: 'All Systems Good',
      detail: 'CPU, memory, and disk are healthy — no streaming issues expected',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      text: '#16a34a',
      iconBg: '#dcfce7'
    };
  };

  const health = getHealthAssessment();

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h2><Activity size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Server Status</h2>
        </div>
        <div className="error-banner" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', color: '#dc2626', marginBottom: '16px' }}>
          Failed to load server status: {error}
          <button className="btn btn-sm btn-secondary" onClick={fetchStatus} style={{ marginLeft: '12px' }}>Retry</button>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div>
        <div className="page-header">
          <h2><Activity size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Server Status</h2>
        </div>
        <p>Loading server metrics...</p>
      </div>
    );
  }

  const HealthIcon = health.icon;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2><Activity size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Server Status</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={() => setAutoRefresh(!autoRefresh)} />
            Auto-refresh (10s)
          </label>
          <button className="btn btn-sm btn-secondary" onClick={fetchStatus}>Refresh</button>
        </div>
      </div>

      {/* Health Assessment Banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        background: health.bg, border: `1px solid ${health.border}`,
        borderRadius: '10px', padding: '16px 20px', marginBottom: '24px'
      }}>
        <div style={{
          background: health.iconBg, borderRadius: '50%',
          width: '44px', height: '44px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <HealthIcon size={24} color={health.text} />
        </div>
        <div>
          <div style={{ fontWeight: '700', fontSize: '16px', color: health.text }}>
            {health.label}
          </div>
          <div style={{ fontSize: '13px', color: health.text, opacity: 0.8, marginTop: '2px' }}>
            {health.detail}
          </div>
        </div>
      </div>

      {/* Primary stat cards */}
      <div className="stats-grid">
        {/* CPU */}
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Cpu size={20} color="#6b7280" />
            <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>CPU Load</h3>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
            {status.cpu.loadAvg[0].toFixed(1)} / {status.cpu.cpuCount} cores
          </div>
          <div style={{ fontSize: '22px', fontWeight: '600', color: pctColor(status.cpu.loadPct) }}>
            {status.cpu.loadPct}%
          </div>
          <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', marginTop: '10px' }}>
            <div style={{ background: pctColor(status.cpu.loadPct), borderRadius: '4px', height: '6px', width: `${Math.min(status.cpu.loadPct, 100)}%`, transition: 'width 0.5s' }} />
          </div>
          <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '12px', color: '#9ca3af' }}>
            <span>1m: {status.cpu.loadAvg[0].toFixed(2)}</span>
            <span>5m: {status.cpu.loadAvg[1].toFixed(2)}</span>
            <span>15m: {status.cpu.loadAvg[2].toFixed(2)}</span>
          </div>
        </div>

        {/* Memory */}
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <MemoryStick size={20} color="#6b7280" />
            <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Memory</h3>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' }}>
            {formatBytes(status.memory.used)} / {formatBytes(status.memory.total)}
          </div>
          <div style={{ fontSize: '22px', fontWeight: '600', color: pctColor(status.memory.usedPct) }}>
            {status.memory.usedPct}%
          </div>
          <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', marginTop: '10px' }}>
            <div style={{ background: pctColor(status.memory.usedPct), borderRadius: '4px', height: '6px', width: `${Math.min(status.memory.usedPct, 100)}%`, transition: 'width 0.5s' }} />
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
            Free: {formatBytes(status.memory.free)} &middot; API RSS: {formatBytes(status.memory.processRss)}
            {status.memory.dockerApi?.usage ? (
              <> &middot; Docker: {status.memory.dockerApi.usage}{status.memory.dockerApi.pct != null ? ` (${status.memory.dockerApi.pct}%)` : ''}</>
            ) : null}
          </div>
        </div>

        {/* Active Streams */}
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Activity size={20} color="#6b7280" />
            <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Active Streams</h3>
          </div>
          <div style={{ fontSize: '48px', fontWeight: 'bold', color: status.streams.active > 0 ? '#f59e0b' : '#10b981' }}>
            {status.streams.active}
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
            streaming now
          </div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
            {status.streams.watchingCustomers != null
              ? `${status.streams.watchingCustomers} customer${status.streams.watchingCustomers === 1 ? '' : 's'} watching`
              : null}
          </div>
        </div>
      </div>

      {/* Per-customer stream breakdown (Issue 118) */}
      {Array.isArray(status.streams.byCustomer) && status.streams.byCustomer.length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <div className="table-header"><h3>Active by customer</h3></div>
          <table className="table">
            <thead><tr><th>Customer ID</th><th>Streams</th><th>Unique IPs</th></tr></thead>
            <tbody>
              {status.streams.byCustomer.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>{c.streams}</td>
                  <td>{c.active_ips}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Secondary info */}
      <div className="stats-grid" style={{ marginTop: '20px' }}>
        {/* Disk */}
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <HardDrive size={20} color="#6b7280" />
            <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Disk</h3>
          </div>
          {status.disk.error ? (
            <div style={{ color: '#9ca3af' }}>Disk info unavailable</div>
          ) : (
            <>
              <div style={{ fontSize: '22px', fontWeight: 'bold' }}>{status.disk.pct || '—'}</div>
              <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', marginTop: '10px' }}>
                <div style={{
                  background: pctColor(parseInt(status.disk.pct) || 0),
                  borderRadius: '4px',
                  height: '6px',
                  width: `${Math.min(parseInt(status.disk.pct) || 0, 100)}%`,
                  transition: 'width 0.5s'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
                {formatBytes(status.disk.used)} used &middot; {formatBytes(status.disk.available)} free
              </div>
            </>
          )}
        </div>

        {/* Uptime */}
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Activity size={20} color="#6b7280" />
            <h3 style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Uptime</h3>
          </div>
          <div style={{ fontSize: '14px' }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#6b7280' }}>System: </span>
              <strong>{formatUptime(status.uptime.system)}</strong>
            </div>
            <div>
              <span style={{ color: '#6b7280' }}>API Process: </span>
              <strong>{formatUptime(status.uptime.process)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#9ca3af', textAlign: 'right' }}>
        Last updated: {new Date(status.timestamp).toLocaleTimeString()}
      </div>
    </div>
  );
}

export default ServerStatus;
